import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Search, Star, CheckCircle, MapPin, Phone,
  ChevronRight, QrCode, Globe, Menu, X, Users,
  Building2, Shield, CreditCard, Sparkles, TrendingUp,
  Navigation, ChevronDown
} from 'lucide-react';

function AnimatedCounter({ end, duration = 2000, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let frame;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [started, end, duration]);

  return (
    <span ref={ref} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

const t = (lang, fr, en) => lang === 'fr' ? fr : en;

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/987d762f-6280-4d2f-beee-1ba5be49cef1/images/cf3ce17456f3599f7b66774ca5e98ed2d49a95335c88f4cffdf5e6f84b99a28f.png';

const PLANS = [
  {
    key: 'free',
    name_fr: 'Gratuit', name_en: 'Free',
    price: '$0',
    period_fr: '/mois', period_en: '/mo',
    features_fr: ['Inscription de base', '1 photo', 'Placement standard', 'Formulaire de contact'],
    features_en: ['Basic listing', '1 photo', 'Standard placement', 'Contact form'],
    cta_fr: 'Commencer', cta_en: 'Get Started',
    highlight: false,
  },
  {
    key: 'silver',
    name_fr: 'Argent', name_en: 'Silver',
    price: '$29',
    period_fr: '/mois', period_en: '/mo',
    features_fr: ['Tout dans Gratuit', '5 photos', 'Fiche amelioree', 'Telephone & courriel visibles', 'Analytiques de base'],
    features_en: ['Everything in Free', '5 photos', 'Enhanced listing', 'Phone & email visible', 'Basic analytics'],
    cta_fr: "S'abonner", cta_en: 'Subscribe',
    highlight: false,
  },
  {
    key: 'gold',
    name_fr: 'Or', name_en: 'Gold',
    price: '$59',
    period_fr: '/mois', period_en: '/mo',
    features_fr: ['Tout dans Argent', '15 photos', 'Badge verifie', 'En vedette dans resultats', 'Notifications prospects', 'Analytiques avancees', 'Liens reseaux sociaux'],
    features_en: ['Everything in Silver', '15 photos', 'Verified badge', 'Featured in results', 'Lead notifications', 'Advanced analytics', 'Social media links'],
    cta_fr: "S'abonner", cta_en: 'Subscribe',
    highlight: true,
    label_fr: 'Le plus populaire', label_en: 'Most Popular',
  },
  {
    key: 'platinum',
    name_fr: 'Platine', name_en: 'Platinum',
    price: '$99',
    period_fr: '/mois', period_en: '/mo',
    features_fr: ['Tout dans Or', 'Photos illimitees', 'Banniere vedette accueil', 'Placement prioritaire #1', 'Gestionnaire dedie', 'Notifications WhatsApp', 'Rapport mensuel', "Acces API"],
    features_en: ['Everything in Gold', 'Unlimited photos', 'Homepage featured banner', 'Priority placement #1', 'Dedicated manager', 'WhatsApp notifications', 'Monthly report', 'API access'],
    cta_fr: "S'abonner", cta_en: 'Subscribe',
    highlight: false,
  },
];

// Read / write user location from localStorage
function getUserLocation() {
  try {
    const raw = localStorage.getItem('localsite_location');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setUserLocation(loc) {
  localStorage.setItem('localsite_location', JSON.stringify(loc));
}

export default function DirectoryLandingPage() {
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({ total_businesses: 0, total_categories: 0, total_cities: 0 });
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  // Location state
  const [userLoc, setUserLoc] = useState(getUserLocation);
  const [showLocPrompt, setShowLocPrompt] = useState(!getUserLocation());
  const [postalInput, setPostalInput] = useState('');
  const [locLookupLoading, setLocLookupLoading] = useState(false);
  const [locError, setLocError] = useState('');

  const loadData = useCallback(async (localCity) => {
    try {
      const bizUrl = localCity
        ? `${API}/api/directory/businesses?limit=6&sort=rating&local_city=${encodeURIComponent(localCity)}`
        : `${API}/api/directory/businesses?limit=6&sort=rating`;

      const [statsRes, bizRes, catRes, testRes] = await Promise.all([
        fetch(`${API}/api/directory/stats`),
        fetch(bizUrl),
        fetch(`${API}/api/directory/categories`),
        fetch(`${API}/api/directory/testimonials`),
      ]);
      const sd = await statsRes.json();
      const bd = await bizRes.json();
      const cd = await catRes.json();
      const td = await testRes.json();
      setStats(sd);
      setFeatured(bd.businesses || []);
      setCategories(cd.categories || []);
      setSubcategories(cd.subcategories || []);
      setTestimonials(td.testimonials || []);
    } catch (e) { console.error('Failed to load directory data:', e); }
  }, []);

  useEffect(() => {
    loadData(userLoc?.city || null);
  }, [loadData, userLoc]);

  const lookupPostalCode = async () => {
    if (!postalInput.trim()) return;
    setLocLookupLoading(true);
    setLocError('');
    try {
      const res = await fetch(`${API}/api/locations/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postal_code: postalInput.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        const loc = {
          city: data.city,
          neighborhood: data.neighborhood,
          province_code: data.province_code,
          province_name_fr: data.province_name_fr,
          province_name_en: data.province_name_en,
          country: data.country,
          postal_code: data.postal_code,
        };
        setUserLoc(loc);
        setUserLocation(loc);
        setShowLocPrompt(false);
        setLocError('');
      } else {
        setLocError(t(language, 'Code postal non reconnu', 'Unrecognized postal code'));
      }
    } catch {
      setLocError(t(language, 'Erreur de connexion', 'Connection error'));
    } finally {
      setLocLookupLoading(false);
    }
  };

  const changeLocation = () => {
    setShowLocPrompt(true);
    setPostalInput('');
    setLocError('');
  };

  const clearLocation = () => {
    setUserLoc(null);
    localStorage.removeItem('localsite_location');
    setShowLocPrompt(false);
    loadData(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    if (userLoc?.city) params.set('city', userLoc.city);
    navigate(`/browse?${params.toString()}`);
  };

  // Count local businesses in featured
  const localCount = userLoc ? featured.filter(b => b.city?.toLowerCase() === userLoc.city?.toLowerCase()).length : 0;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Satoshi', 'IBM Plex Sans', sans-serif" }}>
      {/* ===== NAVIGATION ===== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/60" data-testid="directory-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <div className="w-9 h-9 bg-[#E05D3A] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <span className="font-bold text-[var(--ls-text)] text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>LocalSite</span>
                <span className="text-[#E05D3A] font-bold text-lg">.Club</span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-[var(--ls-text-muted)]">
              <Link to="/browse?category=restaurants" className="hover:text-[#E05D3A] transition-colors" data-testid="nav-restaurants">
                {t(language, 'Restaurants', 'Restaurants')}
              </Link>
              <Link to="/browse?category=home-services" className="hover:text-[#E05D3A] transition-colors" data-testid="nav-home-services">
                {t(language, 'Services', 'Home Services')}
              </Link>
              <Link to="/browse?category=professionals" className="hover:text-[#E05D3A] transition-colors" data-testid="nav-professionals">
                {t(language, 'Professionnels', 'Professionals')}
              </Link>
              <Link to="/browse" className="hover:text-[#E05D3A] transition-colors" data-testid="nav-browse-all">
                {t(language, 'Tout voir', 'Browse All')}
              </Link>
            </nav>

            <div className="hidden md:flex items-center gap-3">
              {/* Location indicator */}
              {userLoc && (
                <button onClick={changeLocation}
                  className="flex items-center gap-1.5 text-sm font-medium text-[#E05D3A] hover:text-[#C44B2A] transition-colors bg-[#E05D3A]/5 px-3 py-1.5 rounded-full"
                  data-testid="nav-location-indicator">
                  <Navigation className="w-3.5 h-3.5" />
                  {userLoc.city}
                  <ChevronDown className="w-3 h-3" />
                </button>
              )}
              <div className="flex bg-gray-100 rounded-md overflow-hidden text-xs font-semibold">
                <button onClick={() => language !== 'fr' && toggleLanguage()}
                  className={`px-2.5 py-1.5 transition-colors ${language === 'fr' ? 'bg-[#E05D3A] text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="lang-toggle-fr">FR</button>
                <button onClick={() => language !== 'en' && toggleLanguage()}
                  className={`px-2.5 py-1.5 transition-colors ${language === 'en' ? 'bg-[#E05D3A] text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="lang-toggle-en">EN</button>
              </div>
              <Link to="/login" className="text-sm font-medium text-[var(--ls-text-muted)] hover:text-[#E05D3A] transition-colors" data-testid="members-login">
                {t(language, 'Membres', 'Members')}
              </Link>
              <Link to="/browse" className="bg-[#E05D3A] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#C44B2A] transition-colors" data-testid="nav-browse-cta">
                {t(language, 'Explorer', 'Browse')}
              </Link>
            </div>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-gray-100" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg animate-in slide-in-from-top-2" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-2">
              {userLoc && (
                <button onClick={() => { changeLocation(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-lg text-[#E05D3A] bg-[#E05D3A]/5 font-medium">
                  <Navigation className="w-4 h-4" />
                  {userLoc.city}{userLoc.neighborhood ? ` (${userLoc.neighborhood})` : ''}
                  <ChevronDown className="w-3 h-3 ml-auto" />
                </button>
              )}
              <Link to="/browse?category=restaurants" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--ls-text)] hover:bg-gray-50 font-medium">
                {t(language, 'Restaurants', 'Restaurants')}
              </Link>
              <Link to="/browse?category=home-services" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--ls-text)] hover:bg-gray-50 font-medium">
                {t(language, 'Services a domicile', 'Home Services')}
              </Link>
              <Link to="/browse?category=professionals" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--ls-text)] hover:bg-gray-50 font-medium">
                {t(language, 'Professionnels', 'Professionals')}
              </Link>
              <Link to="/browse" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[var(--ls-text)] hover:bg-gray-50 font-medium">
                {t(language, 'Tout voir', 'Browse All')}
              </Link>
              <hr className="my-2" />
              <div className="flex items-center justify-between px-4">
                <span className="text-sm text-gray-500">{t(language, 'Langue', 'Language')}</span>
                <div className="flex bg-gray-100 rounded-md overflow-hidden text-xs font-semibold">
                  <button onClick={() => { language !== 'fr' && toggleLanguage(); }}
                    className={`px-3 py-1.5 ${language === 'fr' ? 'bg-[#E05D3A] text-white' : 'text-gray-500'}`}>FR</button>
                  <button onClick={() => { language !== 'en' && toggleLanguage(); }}
                    className={`px-3 py-1.5 ${language === 'en' ? 'bg-[#E05D3A] text-white' : 'text-gray-500'}`}>EN</button>
                </div>
              </div>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-lg text-[#E05D3A] font-semibold">
                {t(language, 'Connexion Membres', 'Members Login')}
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ===== HERO ===== */}
      <section className="relative pt-16" data-testid="hero-section">
        <div className="relative h-[70vh] min-h-[500px] max-h-[700px] overflow-hidden">
          <img src={HERO_BG} alt="City" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-4"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="hero-title">
              {userLoc
                ? t(language,
                    `Decouvrez le meilleur de ${userLoc.city}`,
                    `Discover the best of ${userLoc.city}`)
                : t(language,
                    'Trouvez ce qui compte dans votre ville',
                    "Find what's next in your city")
              }
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-xl mb-8">
              {userLoc
                ? t(language,
                    `Entreprises, services et professionnels pres de chez vous a ${userLoc.city}${userLoc.neighborhood ? ` (${userLoc.neighborhood})` : ''}`,
                    `Businesses, services and professionals near you in ${userLoc.city}${userLoc.neighborhood ? ` (${userLoc.neighborhood})` : ''}`)
                : t(language,
                    "Explorez le meilleur de votre ville et soutenez l'excellence locale",
                    'Explore the best your city has to offer and support local excellence')
              }
            </p>
            <form onSubmit={handleSearch} className="w-full max-w-2xl flex gap-2" data-testid="hero-search-form">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={userLoc
                    ? t(language, `Rechercher a ${userLoc.city}...`, `Search in ${userLoc.city}...`)
                    : t(language, 'Rechercher entreprises, services...', 'Search businesses, services...')
                  }
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white text-[var(--ls-text)] text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E05D3A] shadow-lg"
                  data-testid="hero-search-input"
                />
              </div>
              <button type="submit" className="bg-[#E05D3A] text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-[#C44B2A] transition-colors shadow-lg"
                data-testid="hero-search-button">
                {t(language, 'Rechercher', 'Search')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ===== LOCATION BAR ===== */}
      {(showLocPrompt || !userLoc) && (
        <section className="relative z-30 -mt-7 px-4 sm:px-6 lg:px-8" data-testid="location-prompt">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-[#E05D3A]/10 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#E05D3A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Navigation className="w-5 h-5 text-[#E05D3A]" />
                </div>
                <div>
                  <h3 className="font-bold text-[var(--ls-text)] text-sm">
                    {t(language, 'Ou etes-vous ?', 'Where are you?')}
                  </h3>
                  <p className="text-xs text-[var(--ls-text-muted)]">
                    {t(language,
                      'Entrez votre code postal pour voir les entreprises pres de chez vous',
                      'Enter your postal code to see businesses near you')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={postalInput}
                    onChange={(e) => setPostalInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && lookupPostalCode()}
                    placeholder={t(language, 'Ex: H2X 1Y4 ou 10001', 'Ex: H2X 1Y4 or 10001')}
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-sm text-[var(--ls-text)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E05D3A]/40 focus:border-[#E05D3A]"
                    data-testid="location-postal-input"
                  />
                </div>
                <button onClick={lookupPostalCode} disabled={locLookupLoading}
                  className="bg-[#E05D3A] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#C44B2A] transition-colors disabled:opacity-50"
                  data-testid="location-detect-btn">
                  {locLookupLoading
                    ? t(language, 'Detection...', 'Detecting...')
                    : t(language, 'Detecter', 'Detect')
                  }
                </button>
              </div>
              {locError && <p className="text-red-500 text-xs mt-2" data-testid="location-error">{locError}</p>}
            </div>
          </div>
        </section>
      )}

      {/* ===== LOCATION ACTIVE BAR ===== */}
      {userLoc && !showLocPrompt && (
        <section className="relative z-30 -mt-7 px-4 sm:px-6 lg:px-8" data-testid="location-active-bar">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#E05D3A] rounded-2xl shadow-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Navigation className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">
                    {userLoc.city}
                    {userLoc.neighborhood && <span className="font-normal text-white/70"> &middot; {userLoc.neighborhood}</span>}
                  </p>
                  <p className="text-white/60 text-xs">
                    {language === 'fr' ? userLoc.province_name_fr : userLoc.province_name_en}, {userLoc.country === 'CA' ? 'Canada' : 'USA'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={changeLocation}
                  className="text-white/80 hover:text-white text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
                  data-testid="change-location-btn">
                  {t(language, 'Changer', 'Change')}
                </button>
                <button onClick={clearLocation}
                  className="text-white/50 hover:text-white/80 transition-colors p-1"
                  data-testid="clear-location-btn"
                  title={t(language, 'Voir tout le repertoire', 'See full directory')}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== STATS COUNTER ===== */}
      <section className={`relative z-20 px-4 sm:px-6 lg:px-8 ${userLoc && !showLocPrompt ? 'mt-4' : (!showLocPrompt && !userLoc) ? '-mt-14' : 'mt-4'}`} data-testid="stats-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
            {[
              { end: 5000, suffix: '+', label_fr: 'Entreprises inscrites', label_en: 'Businesses Listed', icon: Building2 },
              { end: 50, suffix: '+', label_fr: 'Categories', label_en: 'Categories', icon: Search },
              { end: 25000, suffix: '+', label_fr: 'Clients satisfaits', label_en: 'Happy Customers', icon: Users },
              { end: 98, suffix: '%', label_fr: 'Taux de satisfaction', label_en: 'Satisfaction Rate', icon: TrendingUp },
            ].map((stat) => (
              <div key={stat.label_en} className="flex flex-col items-center py-7 px-4 text-center" data-testid={`stat-counter-${stat.label_en}`}>
                <stat.icon className="w-5 h-5 text-[#E05D3A] mb-2" />
                <p className="text-2xl sm:text-3xl font-bold text-[var(--ls-text)] tabular-nums">
                  <AnimatedCounter end={stat.end} suffix={stat.suffix} duration={1800} />
                </p>
                <p className="text-xs sm:text-sm text-[var(--ls-text-muted)] mt-1 font-medium">
                  {t(language, stat.label_fr, stat.label_en)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CATEGORIES ===== */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#E05D3A] font-semibold mb-2">
                {t(language, 'Votre carnet d\'adresses', 'Your Address Book')}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--ls-text)] tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {userLoc
                  ? t(language, `Services disponibles a ${userLoc.city}`, `Available services in ${userLoc.city}`)
                  : t(language, 'Pour chaque besoin local', 'For Every Local Need')
                }
              </h2>
            </div>
            <Link to="/browse" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-[#E05D3A] hover:underline" data-testid="categories-view-all">
              {t(language, 'Tout voir', 'View all')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-[var(--ls-text-muted)] mb-8 max-w-2xl">
            {t(language,
              'Trouvez et connectez-vous facilement avec les entreprises les mieux notees dans tous les secteurs.',
              'Easily find and connect with top-rated businesses across various industries.'
            )}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {categories.map((cat) => (
              <Link key={cat.key} to={`/browse?category=${cat.key}${userLoc ? `&city=${encodeURIComponent(userLoc.city)}` : ''}`}
                className="group relative h-52 rounded-2xl overflow-hidden cursor-pointer"
                data-testid={`category-card-${cat.key}`}>
                <img src={cat.image} alt={language === 'fr' ? cat.name_fr : cat.name_en}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-white font-bold text-lg">{language === 'fr' ? cat.name_fr : cat.name_en}</h3>
                </div>
              </Link>
            ))}
          </div>

          <div className="flex flex-wrap gap-2.5">
            {subcategories.map((sub) => (
              <Link key={sub.key} to={`/browse?subcategory=${sub.key}${userLoc ? `&city=${encodeURIComponent(userLoc.city)}` : ''}`}
                className="px-4 py-2 rounded-full border border-gray-200 text-sm text-[var(--ls-text-muted)] hover:bg-[#E05D3A] hover:text-white hover:border-[#E05D3A] transition-all duration-200"
                data-testid={`subcategory-pill-${sub.key}`}>
                {language === 'fr' ? sub.name_fr : sub.name_en}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED BUSINESSES ===== */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--ls-sand-dark)]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#E05D3A] font-semibold mb-2">
                {userLoc
                  ? t(language, `Pres de chez vous a ${userLoc.city}`, `Near you in ${userLoc.city}`)
                  : t(language, 'Mise en lumiere', 'Search Smarter. Connect Locally.')
                }
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--ls-text)] tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {userLoc
                  ? t(language, 'Entreprises dans votre secteur', 'Businesses in your area')
                  : t(language, 'Entreprises en vedette', 'Explore the Best in Your Community')
                }
              </h2>
            </div>
            <Link to={`/browse${userLoc ? `?city=${encodeURIComponent(userLoc.city)}` : ''}`} className="hidden sm:flex items-center gap-1 text-sm font-semibold text-[#E05D3A] hover:underline" data-testid="featured-view-all">
              {t(language, 'Tout voir', 'View all')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {featured.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.slice(0, 6).map((biz) => {
                const isLocal = userLoc && biz.city?.toLowerCase() === userLoc.city?.toLowerCase();
                return (
                  <Link key={biz.slug} to={`/business/${biz.slug}`}
                    className={`group bg-white rounded-2xl overflow-hidden border hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${isLocal ? 'border-[#E05D3A]/30 ring-1 ring-[#E05D3A]/10' : 'border-gray-100'}`}
                    data-testid={`business-card-${biz.slug}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img src={biz.image_url} alt={biz.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute top-3 left-3 flex gap-2">
                        {biz.rating > 0 && (
                          <span className="flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-[var(--ls-text)]">
                            <Star className="w-3.5 h-3.5 text-[#FFB300] fill-[#FFB300]" /> {biz.rating}
                          </span>
                        )}
                        {isLocal && (
                          <span className="flex items-center gap-1 bg-[#E05D3A]/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-white">
                            <Navigation className="w-3 h-3" /> {t(language, 'Local', 'Local')}
                          </span>
                        )}
                      </div>
                      {biz.is_verified ? (
                        <span className="absolute top-3 right-3 flex items-center gap-1 bg-[#10B981]/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-white">
                          <CheckCircle className="w-3.5 h-3.5" /> {t(language, 'Verifie', 'Verified')}
                        </span>
                      ) : (
                        <span className="absolute top-3 right-3 flex items-center gap-1 bg-[#6B7280]/80 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-white">
                          {t(language, 'Invite', 'Invited')}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <span className="inline-block text-xs font-medium text-[#E05D3A] bg-[#E05D3A]/8 rounded-full px-3 py-1 mb-2">
                        {language === 'fr'
                          ? (subcategories.find(s => s.key === biz.subcategory)?.name_fr || categories.find(c => c.key === biz.category)?.name_fr || biz.category)
                          : (subcategories.find(s => s.key === biz.subcategory)?.name_en || categories.find(c => c.key === biz.category)?.name_en || biz.category)
                        }
                      </span>
                      <h3 className="font-bold text-[var(--ls-text)] text-lg mb-1">{biz.name}</h3>
                      <div className="flex items-center gap-1.5 text-sm text-[var(--ls-text-muted)] mb-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {biz.city}
                        {biz.neighborhood && <span className="text-gray-400">&middot; {biz.neighborhood}</span>}
                      </div>
                      {biz.phone && (
                        <div className="flex items-center gap-1.5 text-sm text-[var(--ls-text-muted)]">
                          <Phone className="w-3.5 h-3.5" /> {biz.phone}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-[var(--ls-text-muted)]">{t(language, 'Aucune entreprise en vedette pour le moment.', 'No featured businesses yet.')}</p>
            </div>
          )}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      {testimonials.length > 0 && (
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs tracking-[0.2em] uppercase text-[#E05D3A] font-semibold mb-2">
                {t(language, 'Ce que les gens disent', 'What People Are Saying')}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--ls-text)] tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t(language, 'A propos des favoris locaux', 'About Local Favorites')}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((test, i) => (
                <div key={test.testimonial_id || i}
                  className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-shadow"
                  data-testid={`testimonial-${i}`}>
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className={`w-4 h-4 ${s < test.rating ? 'text-[#FFB300] fill-[#FFB300]' : 'text-gray-200'}`} />
                    ))}
                  </div>
                  <h4 className="font-bold text-[var(--ls-text)] text-sm mb-2">
                    {language === 'fr' ? test.title_fr : test.title_en}
                  </h4>
                  <p className="text-sm text-[var(--ls-text-muted)] leading-relaxed mb-4 line-clamp-3">
                    {language === 'fr' ? test.text_fr : test.text_en}
                  </p>
                  <div className="flex items-center justify-between text-xs text-[var(--ls-text-muted)]">
                    <span className="font-medium">{test.author} &middot; {test.date}</span>
                    <Link to={`/business/${test.business_slug}`} className="text-[#E05D3A] font-semibold hover:underline">
                      {test.business_name}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== NOTRE MISSION ===== */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[#E05D3A]" data-testid="mission-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-[0.2em] uppercase text-white/50 font-semibold mb-4">
            {t(language, 'Notre mission', 'Our Mission')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl text-white leading-relaxed font-medium"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t(language,
              "Notre Collectif en ligne, LocalSite.Club a une mission simple : booster le local economique ! On soutient les producteurs et artisans de chez nous en valorisant leurs produits uniques et authentiques. Acheter local, c'est preserver nos richesses, notre argent, reduire notre impact ecologique et creer des liens dans nos communautes. On croit fermement qu'en consommant chez-nous, on fait tourner l'economie locale tout en savourant la qualite et la fraicheur. Alors, rejoins le mouvement LocalSite.Club, choisis le local et fais du bien a ta region avec le sourire !",
              "An online collective with a simple mission: boosting local economies! Let's support our local producers and artisans by showcasing their unique and authentic products. Buying local means preserving our treasures, our money, reducing our ecological footprint, and building stronger community ties. We firmly believe that by consuming local, we keep our economy thriving while enjoying quality and freshness. So join the movement, choose LocalSite.Club, and do good for your region — with a smile!"
            )}
          </p>
        </div>
      </section>

      {/* ===== LOCALSITE PASS (QR) ===== */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--ls-bark)]" data-testid="localsite-pass-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#E05D3A] font-semibold mb-3">
                {t(language, 'Exclusif', 'Exclusive')}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-6"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t(language, 'Le LocalSite Pass', 'The LocalSite Pass')}
              </h2>
              <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-8">
                {t(language,
                  "Votre carte membre virtuelle avec QR Code. Presentez-la chez les commercants participants et obtenez 10% de rabais instantane. Accumulez des points et profitez d'avantages exclusifs.",
                  'Your virtual member card with QR Code. Present it at participating businesses and get an instant 10% discount. Accumulate points and enjoy exclusive benefits.'
                )}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: QrCode, fr: '10% de rabais instantane', en: '10% instant discount' },
                  { icon: CreditCard, fr: 'Carte virtuelle QR', en: 'Virtual QR card' },
                  { icon: Sparkles, fr: 'Points et recompenses', en: 'Points & rewards' },
                  { icon: Shield, fr: '100% gratuit pour vous', en: '100% free for you' },
                ].map((item) => (
                  <div key={item.en} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E05D3A]/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#E05D3A]" />
                    </div>
                    <span className="text-white text-sm font-medium">{t(language, item.fr, item.en)}</span>
                  </div>
                ))}
              </div>
              <Link to="/browse" className="inline-flex items-center gap-2 bg-[#E05D3A] text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-[#C44B2A] transition-colors"
                data-testid="pass-cta">
                {t(language, 'Obtenir mon pass', 'Get My Pass')}
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-72 h-44 rounded-2xl bg-gradient-to-br from-[#E05D3A] to-[#00227A] p-6 shadow-2xl shadow-[#E05D3A]/30 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-white/60 text-[10px] tracking-widest uppercase">Member Card</p>
                      <p className="text-white font-bold text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>LocalSite.Club</p>
                    </div>
                    <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center">
                      <QrCode className="w-10 h-10 text-[#E05D3A]" />
                    </div>
                  </div>
                  <div className="mt-auto">
                    <p className="text-white/60 text-[10px] tracking-widest uppercase mb-0.5">{t(language, 'Membre', 'Member')}</p>
                    <p className="text-white font-semibold">Jean-Pierre Dupont</p>
                  </div>
                  <div className="absolute bottom-3 right-6 text-white/40 text-[10px]">ID: LSC-2026-0001</div>
                </div>
                <div className="absolute -z-10 w-72 h-44 rounded-2xl bg-[#E05D3A]/20 top-4 left-4" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRICING PLANS ===== */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase text-[#E05D3A] font-semibold mb-2">
              {t(language, "Plans d'adhesion", 'Membership Plans')}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--ls-text)] tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t(language, 'Developpez votre entreprise', 'Grow Your Business')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANS.map((plan) => (
              <div key={plan.key}
                className={`relative rounded-2xl border p-6 flex flex-col ${
                  plan.highlight
                    ? 'border-[#E05D3A] bg-white shadow-xl shadow-[#E05D3A]/10 ring-2 ring-[#E05D3A]'
                    : 'border-gray-200 bg-white hover:shadow-lg'
                } transition-shadow`}
                data-testid={`pricing-${plan.key}`}>
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E05D3A] text-white text-xs font-bold px-4 py-1 rounded-full">
                    {t(language, plan.label_fr, plan.label_en)}
                  </span>
                )}
                <h3 className="font-bold text-lg text-[var(--ls-text)] mb-1">
                  {t(language, plan.name_fr, plan.name_en)}
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-[var(--ls-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {plan.price}
                  </span>
                  <span className="text-sm text-[var(--ls-text-muted)]">{t(language, plan.period_fr, plan.period_en)}</span>
                </div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {(language === 'fr' ? plan.features_fr : plan.features_en).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[var(--ls-text-muted)]">
                      <CheckCircle className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                  plan.highlight
                    ? 'bg-[#E05D3A] text-white hover:bg-[#C44B2A]'
                    : 'bg-gray-100 text-[var(--ls-text)] hover:bg-gray-200'
                }`}
                  data-testid={`pricing-${plan.key}-cta`}>
                  {t(language, plan.cta_fr, plan.cta_en)}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[#F3F4F6]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[var(--ls-text)] tracking-tight mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t(language, "Pret a developper votre entreprise ?", 'Ready to grow your business?')}
          </h2>
          <p className="text-[var(--ls-text-muted)] text-base sm:text-lg mb-8 max-w-xl mx-auto">
            {t(language,
              "Rejoignez des milliers d'entreprises locales et connectez-vous avec plus de clients.",
              'Join thousands of local businesses and connect with more customers.'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/browse" className="inline-flex items-center justify-center gap-2 bg-[#E05D3A] text-white px-8 py-3.5 rounded-xl font-semibold hover:bg-[#C44B2A] transition-colors"
              data-testid="cta-browse">
              {t(language, 'Explorer les entreprises', 'Browse Listings')}
            </Link>
            <button className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-[var(--ls-text)] px-8 py-3.5 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              data-testid="cta-add-business">
              {t(language, 'Inscrire mon entreprise', 'Add Your Business')} <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[var(--ls-bark)] text-white py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-testid="directory-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 bg-[#E05D3A] rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  LocalSite<span className="text-[#E05D3A]">.Club</span>
                </span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t(language,
                  "Connectez-vous aux entreprises et professionnels de confiance dans votre ville.",
                  'Connecting you to trusted businesses and professionals in your city.'
                )}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">{t(language, 'Explorer', 'Explore')}</h4>
              <div className="space-y-2.5">
                <Link to="/browse" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Toutes les entreprises', 'Browse Listings')}</Link>
                <Link to="/browse?category=restaurants" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Restaurants', 'Restaurants')}</Link>
                <Link to="/browse?category=home-services" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Services a domicile', 'Home Services')}</Link>
                <Link to="/browse?category=professionals" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Professionnels', 'Professionals')}</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">{t(language, 'Pour les entreprises', 'For Business')}</h4>
              <div className="space-y-2.5">
                <button className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Inscrire mon entreprise', 'List Your Business')}</button>
                <Link to="/login" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Connexion entreprise', 'Business Login')}</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider mb-4">{t(language, 'Plateforme', 'Platform')}</h4>
              <div className="space-y-2.5">
                <Link to="/login" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Connexion Membres', 'Members Login')}</Link>
                <Link to="/affiliation" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Programme d\'affiliation', 'Affiliate Program')}</Link>
                <Link to="/login" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'Espace Affilies', 'Affiliate Portal')}</Link>
                <Link to="/mlm" className="block text-gray-500 hover:text-white text-sm transition-colors">{t(language, 'MLM Matrix 4x7', 'MLM Matrix 4x7')}</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-sm">
              &copy; 2026 LocalSite.Club. {t(language, 'Tous droits reserves.', 'All rights reserved.')}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex bg-gray-800 rounded-md overflow-hidden text-xs font-semibold">
                <button onClick={() => language !== 'fr' && toggleLanguage()}
                  className={`px-3 py-1.5 transition-colors ${language === 'fr' ? 'bg-[#E05D3A] text-white' : 'text-gray-500'}`}>FR</button>
                <button onClick={() => language !== 'en' && toggleLanguage()}
                  className={`px-3 py-1.5 transition-colors ${language === 'en' ? 'bg-[#E05D3A] text-white' : 'text-gray-500'}`}>EN</button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
